import React, { useState, useEffect, useContext } from 'react';
import { Box, Stack, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, CircularProgress } from '@mui/material';
import { Table, TableBody, TableContainer, TableHead, TableRow, Typography, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseFormContext } from '../utils/contexts/nurseFormContext';
import {
    GetDayJSValueFromUTCDate, GetDateFromDayJSValue, GetDateOnlyStringValue, GetNoToCurrencyFormat, GetNumberFromCurrencyFormat,
    NurseNursePendingSalaryPayAPIUrl, NurseSalaryPaymentAPIUrl, NurseSalaryPaySearchAPIUrl
} from '../utils/extensions/functionEx';
import dayjs from 'dayjs';
import AttachmentBox from '../shared/components/attachmentBox'
import { ServiceNurseStatus } from '../utils/extensions/constantEx';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import { useCookies } from 'react-cookie';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

export default function NurseSalaryPaymentForm(props) {
    const { isOpen, nurseId, handleClose, handlePageLoadingOpen, handlePageLoadingClose, selectedPayment } = props;

    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { SetSalaryPayments } = useContext(NurseFormContext);

    const [cleared, setCleared] = useState(false);
    const [payDate, setPayDate] = useState(dayjs());
    const [receiptNo, setReceiptNo] = useState(0);
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [pendingServicesPays, setPendingServicesPays] = useState([]);
    const [isSaveInProgress, setIsSaveInProgress] = useState(false);
    const [totalBalance, setTotalBalance] = useState('0');
    const [isTotalPayError, setIsTotalPayError] = useState(false);
    const [attchment, setAttchment] = useState(null);
    const [removedDocIds, setRemovedDocIds] = useState([]);
    const [removedPayDocIds, setRemovedPayDocIds] = useState([]);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (isOpen && nurseId > 0) {
            handlePageLoadingOpen();
            fetchPendingSalaryPayments();

            if (selectedPayment) {
                setPayDate(GetDayJSValueFromUTCDate(selectedPayment.pay_date));
                setReceiptNo(selectedPayment.receipt_no);
                setAmount(GetNoToCurrencyFormat(parseFloat(selectedPayment.amount)));
                setNotes(selectedPayment.remarks);

                if (selectedPayment.documents && selectedPayment.documents.length > 0) {
                    setAttchment({
                        id: selectedPayment.documents[0].id,
                        uploadedFileId: selectedPayment.documents[0].document_id,
                        contentType: selectedPayment.documents[0].content_type,
                        documentType: selectedPayment.documents[0].document_type,
                        docNo: selectedPayment.documents[0].document_no,
                        notes: selectedPayment.documents[0].notes,
                        fileName: selectedPayment.documents[0].file_name
                    });
                }
            }
        }
    }, [nurseId, isOpen]);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    useEffect(() => {
        const tempTotalBalance = parseFloat(GetNumberFromCurrencyFormat(totalBalance));
        const tempAmount = parseFloat(GetNumberFromCurrencyFormat(amount));

        setIsTotalPayError(tempAmount <= 0 || tempAmount > tempTotalBalance ? true : false);
    }, [amount, totalBalance]);


    const fetchPendingSalaryPayments = async () => {
        try {
            const tempPendingSalaryPays = [];
            let tempTotalBalance = 0;

            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseNursePendingSalaryPayAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.ok) {
                const tempDbPayments = await response.json();

                for (const tempDbPayment of tempDbPayments) {
                    const tempPayServiceNurse = selectedPayment && selectedPayment.service_nurses && selectedPayment.service_nurses.find(pay => pay.service_nurse_id === tempDbPayment.service_nurse_id);

                    if (!tempPayServiceNurse) {
                        tempTotalBalance += parseFloat(tempDbPayment.balance);
                        tempPendingSalaryPays.push({ ...tempDbPayment, payAmount: '0', isPayAmountError: false });
                    }
                }

                if (selectedPayment && selectedPayment.service_nurses) {
                    for (const tempDbServiceNurse of selectedPayment.service_nurses) {
                        tempTotalBalance += parseFloat(tempDbServiceNurse.balance);
                        tempPendingSalaryPays.splice(0, 0, { ...tempDbServiceNurse, payAmount: GetNoToCurrencyFormat(parseFloat(tempDbServiceNurse.amount)), isPayAmountError: false });
                    }
                }
            }
            setPendingServicesPays([...tempPendingSalaryPays]);
            setTotalBalance(GetNoToCurrencyFormat(tempTotalBalance));
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            handlePageLoadingClose();
        }
    }

    const setPendingServicePayIdAmount = (salaryPayId, payAmount) => {
        const tempPendingSalaryPays = [...pendingServicesPays]
        const tempPayIdAmount = tempPendingSalaryPays.find(pay => pay.id === salaryPayId);

        if (tempPayIdAmount) {
            tempPayIdAmount.payAmount = payAmount

            const tempPayAmount = GetNumberFromCurrencyFormat(payAmount);
            const tempBalance = parseFloat(tempPayIdAmount.balance);

            tempPayIdAmount.isPayAmountError = tempPayAmount > tempBalance ? true : false;
        }
        setPendingServicesPays([...tempPendingSalaryPays]);
    }

    const adjustTotalPayAmount = (totalPayAmount) => {
        const tempPendingSalaryPays = [...pendingServicesPays]

        for (const tempPendingPay of tempPendingSalaryPays) {
            const tempBalance = parseFloat(tempPendingPay.balance);
            tempPendingPay.isPayAmountError = false;

            if (tempBalance >= totalPayAmount) {
                tempPendingPay.payAmount = GetNoToCurrencyFormat(totalPayAmount);
                totalPayAmount = 0;
            } else {
                tempPendingPay.payAmount = GetNoToCurrencyFormat(tempBalance);
                totalPayAmount -= tempBalance;
            }

            if (totalPayAmount <= 0) {
                break;
            }
        }

        setPendingServicesPays([...tempPendingSalaryPays]);
    }


    const handleSaveClick = async () => {
        try {
            if (!payDate || !receiptNo || receiptNo.toString().trim().length === 0 || !amount || amount.trim().length === 0) {
                OpenErrorAlert('Enter valid payment details.')
                return;
            }
            const tempAmount = GetNumberFromCurrencyFormat(amount);
            if (tempAmount <= 0 || isTotalPayError) {
                OpenErrorAlert('Enter valid payment amount.')
                return;
            }
            const tempPendingSalaryPays = [...pendingServicesPays];
            const isPayAmountErrorFound = tempPendingSalaryPays.includes(pay => pay.isPayAmountError);
            if (isPayAmountErrorFound) {
                OpenErrorAlert('Enter valid pay amount for all services.');
                return;
            }

            setIsSaveInProgress(true);

            const tempPayment = {
                id: 0,
                pay_type: 'NurseSalary',
                nurse_id: nurseId,
                receipt_no: receiptNo,
                pay_date: GetDateFromDayJSValue(payDate),
                amount: tempAmount,
                remarks: notes.trim(),
                removed_doc_ids: [...removedDocIds],
                removed_pay_doc_ids: [...removedPayDocIds],
                documents: [],
                service_nurses: []
            };

            for (const tempPendingPay of tempPendingSalaryPays) {
                if (tempPendingPay.payAmount && tempPendingPay.payAmount.trim().length > 0) {
                    const tempPayAmount = GetNumberFromCurrencyFormat(tempPendingPay.payAmount);
                    if (tempPayAmount > 0) {
                        tempPayment.service_nurses.push({
                            id: tempPendingPay.id,
                            nurse_salary_payment_id: 0,
                            service_nurse_id: tempPendingPay.service_nurse_id,
                            amount: tempPayAmount
                        });
                    }
                }
            }

            if (attchment && attchment.id === 0) {
                tempPayment.documents.push({
                    id: 0,
                    document_id: attchment.uploadedFileId,
                    document_type: attchment.documentType,
                    document_no: '',
                    notes: ''
                });
            }
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            let response;

            if (selectedPayment && selectedPayment.id > 0) {
                tempPayment.id = selectedPayment.id;

                response = await fetch(NurseSalaryPaymentAPIUrl(homeState.loggedUser.companyCode), {
                    method: "PUT",
                    headers: headers,
                    body: JSON.stringify(tempPayment)
                });
            } else {
                response = await fetch(NurseSalaryPaymentAPIUrl(homeState.loggedUser.companyCode), {
                    method: "POST",
                    headers: headers,
                    body: JSON.stringify(tempPayment)
                });
            }

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.ok) {
                OpenSuccessAlert('Nurse salary payment saved successfully.');
                await fetchNurseSalaryPayments();
                handleClose();

                return;
            }
            ShowAPIError(response);
        }
        catch (error) {
            OpenErrorAlert(error)
        }
        finally {
            setIsSaveInProgress(false);
        }
    }

    const handleDocSaveClick = (attachValue) => {
        setAttchment(attachValue);
    }

    const handleDocDeleteClick = () => {
        const tempRemovedDocIds = [...removedDocIds];
        const tempRemovedPayDocIds = [...removedPayDocIds];

        if (attchment.uploadedFileId > 0) {
            setRemovedDocIds([...tempRemovedDocIds, attchment.uploadedFileId]);
        }
        if (attchment.id > 0) {
            setRemovedPayDocIds([...tempRemovedPayDocIds, attchment.id]);
        }
        setAttchment(null);
    }


    async function fetchNurseSalaryPayments() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseSalaryPaySearchAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetSalaryPayments(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>
                <BootstrapDialog
                    onClose={handleClose}
                    open={isOpen}
                    fullWidth={true}
                    maxWidth={"lg"}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title-nurse-salary-payment-form">
                        Nurse Salary Payment Form
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Stack>
                            <Box sx={{ paddingTop: '14px' }}>
                                <Stack spacing={2}>
                                    <Grid container spacing={2}>
                                        <Grid size={3}>
                                            <DatePicker format="DD/MM/YYYY"
                                                label="Payment Date" readOnly={false}
                                                slotProps={{
                                                    textField: {
                                                        size: "small", fullWidth: true, required: true,
                                                        error: !payDate ? true : false,
                                                        helperText: !payDate ? 'Enter payment date' : ''
                                                    },
                                                    field: {
                                                        clearable: true,
                                                        onClear: () => setCleared(true)
                                                    }
                                                }}
                                                value={payDate} minDate={dayjs().add(-3, 'day')} maxDate={dayjs()}
                                                onChange={(newValue) => setPayDate(newValue)}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <TextField size="small" fullWidth required type='number'
                                                label="Receipt No"
                                                value={receiptNo}
                                                onChange={(event) => {
                                                    setReceiptNo(event.target.value);
                                                }}
                                                slotProps={{
                                                    input: {
                                                        readOnly: false
                                                    },
                                                }}
                                                error={receiptNo < 0 ? true : false}
                                                helperText={receiptNo < 0 ? 'Enter valid receipt no' : ''}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <TextField size="small" fullWidth
                                                label="Total Balance"
                                                value={totalBalance}
                                                slotProps={{
                                                    input: {
                                                        readOnly: false
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    justifyContent: "space-between",
                                                    alignItems: "flex-start",
                                                }}
                                            >
                                                <TextField size="small" fullWidth
                                                    label="Amount"
                                                    value={amount}
                                                    error={isTotalPayError}
                                                    helperText={isTotalPayError ? 'Invalid amount' : ''}
                                                    onChange={(event) => {
                                                        setAmount(event.target.value);
                                                    }}
                                                    onBlur={(event) => {
                                                        const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                        setAmount(tempFormValue);
                                                        adjustTotalPayAmount(event.target.value ?? 0);
                                                    }}
                                                    onFocus={(event) => {
                                                        if (amount > 0) {
                                                            const tempValue = parseFloat(event.target.value ?? 0);
                                                            if (tempValue > 0) {
                                                                setAmount(GetNumberFromCurrencyFormat(event.target.value));
                                                            } else {
                                                                setAmount('0');
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Stack>
                                        </Grid>
                                        <Grid size={12}>
                                            <TextField size="small" fullWidth multiline
                                                label="Notes" rows={4}
                                                value={notes}
                                                onChange={(event) => {
                                                    setNotes(event.target.value);
                                                }}
                                                slotProps={{
                                                    input: {
                                                        readOnly: false
                                                    },
                                                }}
                                            />
                                        </Grid>

                                    </Grid>
                                    <Grid container spacing={1}>
                                        <Grid size={12}>
                                            <Typography variant="subtitle2" sx={{ paddingTop: '8px' }}>
                                                Nurse Services
                                            </Typography>
                                        </Grid>
                                        <Grid size={12}>

                                            <TableContainer component={Paper} sx={{ maxHeight: '77vh', borderRadius: '0' }}>
                                                <Table stickyHeader sx={{ minWidth: 700, width: '2390px', overflowX: 'auto' }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <StyledTableCell sx={{ width: '100px' }}>Salary</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>Ext. Days Salary</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>Paid</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>Balance</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>Pay Amount</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '220px' }}>Patient</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '150px' }}>Duty</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '70px' }}>Duty Days</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '120px' }}>Ext. Duty Days</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>Start Date</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>End Date</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>Completed Date</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '100px' }}>Completed Days</StyledTableCell>
                                                            <StyledTableCell sx={{ width: '200px' }}>Place</StyledTableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {pendingServicesPays.map((row) => (
                                                            <StyledTableRow key={row.id.toString()}>
                                                                <StyledTableCell>{GetNoToCurrencyFormat(row.total_salary)}</StyledTableCell>
                                                                <StyledTableCell>{GetNoToCurrencyFormat(row.extended_duty_days_salary)}</StyledTableCell>
                                                                <StyledTableCell>{GetNoToCurrencyFormat(row.paid_salary)}</StyledTableCell>
                                                                <StyledTableCell>{GetNoToCurrencyFormat(row.balance)}</StyledTableCell>
                                                                <StyledTableCell>
                                                                    <TextField size="small" fullWidth
                                                                        variant="standard" error={row.isPayAmountError}
                                                                        value={row.payAmount}
                                                                        onChange={(event) => {
                                                                            setPendingServicePayIdAmount(row.id, event.target.value);
                                                                        }}
                                                                        onBlur={(event) => {
                                                                            const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                                            setPendingServicePayIdAmount(row.id, tempFormValue);
                                                                        }}
                                                                        onFocus={(event) => {
                                                                            if (amount > 0) {
                                                                                const tempValue = parseFloat(event.target.value ?? 0);
                                                                                if (tempValue > 0) {
                                                                                    setPendingServicePayIdAmount(row.id, GetNumberFromCurrencyFormat(event.target.value));
                                                                                } else {
                                                                                    setPendingServicePayIdAmount(row.id, '0');
                                                                                }
                                                                            }
                                                                        }}
                                                                    />
                                                                </StyledTableCell>
                                                                <StyledTableCell component="th" scope="row">
                                                                    {row.patient_name}
                                                                </StyledTableCell>
                                                                <StyledTableCell>{row.service_type_name}</StyledTableCell>
                                                                <StyledTableCell>{row.duty_days}</StyledTableCell>
                                                                <StyledTableCell>{row.extended_duty_days}</StyledTableCell>
                                                                <StyledTableCell>{GetDateOnlyStringValue(GetDayJSValueFromUTCDate(row.start_date))}</StyledTableCell>
                                                                <StyledTableCell>{GetDateOnlyStringValue(GetDayJSValueFromUTCDate(row.end_date))}</StyledTableCell>
                                                                <StyledTableCell>
                                                                    {
                                                                        row.service_nurse_status_id == ServiceNurseStatus.Active ? '' :
                                                                            GetDateOnlyStringValue(GetDayJSValueFromUTCDate(row.completed_date))
                                                                    }
                                                                </StyledTableCell>
                                                                <StyledTableCell>
                                                                    {
                                                                        row.service_nurse_status_id === ServiceNurseStatus.Active ? '' : row.completed_days.toString()
                                                                    }
                                                                </StyledTableCell>
                                                                <StyledTableCell>{row.place}</StyledTableCell>

                                                            </StyledTableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                        <Grid size={12}>
                                            <Box sx={{ paddingTop: '14px' }}>
                                                <AttachmentBox selectedFile={attchment} docType="SalaryReceipt"
                                                    handleSaveClick={handleDocSaveClick} handleDeleteClick={handleDocDeleteClick}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {
                            isSaveInProgress && <CircularProgress size="30px" />
                        }
                        <Button variant="contained" onClick={handleSaveClick}>
                            Save
                        </Button>
                    </DialogActions>
                </BootstrapDialog> </LocalizationProvider>
        </React.Fragment>

    );
}
